import { BulkSerialUploadSchema } from '@company/schemas/SerialBulkSchema';
import { EXCEL_COL_ORDER, EXCEL_VALIDATION_CONFIG, SERIAL_NUMBER_REGEX } from '@extendedWarranty/constants';
import {
    useSaveSerialNumbersMutation,
    useValidateSerialNumbersMutation,
} from '@extendedWarranty/services/extendedWarranty';
import {
    CustomFile,
    EWValidationError,
    ExtendedWarrantyForm,
    ExtendedWarrantyFormData,
    IExtendedWarrantyFile,
    InstallationData,
    InstallationDto,
    InstallationDtoByFile,
    ISerialSubmission,
} from '@extendedWarranty/types/extendedWarranty';
import { handleError, toaster } from '@utils/common';
import { addDays, format, isAfter, isEqual, isValid, parse } from 'date-fns';
import { useFormik } from 'formik';
import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { DropzoneState, useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';

enum ViewIndex {
    UploadSerialNumbers = 1,
    ActivationOverview = 2,
    Confirmation = 3,
}

const ExtendedWarrantyContext = createContext<
    | {
          formik: ExtendedWarrantyForm;
          lockNameField: boolean;
          setLockNameField: (value: boolean) => void;
          lockDateField: boolean;
          setLockDateField: (value: boolean) => void;
          isProcessingFiles: boolean;
          setIsProcessingFiles: (value: boolean) => void;
          dropzoneState: DropzoneState | null;
          isValidateSerialNumbersLoading: boolean;
          isSaveSerialNumbersLoading: boolean;
          viewIndex: ViewIndex;
          setViewIndex: (value: ViewIndex) => void;
          installations: Record<string, InstallationData>;
          setInstallations: (value: Record<string, InstallationData>) => void;
          validateInstallations: (installationSerialNumbers: InstallationDto) => Promise<{
              status: string;
              result: Record<string, InstallationData>;
          }>;
          processDuplicates: (result: Record<string, InstallationData>) => Record<string, InstallationData>;
      }
    | undefined
>(undefined);

const useExtendedWarrantyContext = () => {
    const context = useContext(ExtendedWarrantyContext);
    if (!context) throw new Error('useExtendedWarrantyContext must be used within a provider');
    return context;
};

const processDuplicates = (result: Record<string, InstallationData>) => {
    // Check for duplicates, for each installation, for each serial number
    const serialNumbers = Object.keys(result).reduce(
        (acc, curr) => [...acc, ...result[curr].serial_numbers],
        [] as ISerialSubmission[]
    );

    const processedResult = Object.keys(result).reduce((acc, curr) => {
        const isDuplicate = (sn: ISerialSubmission) =>
            !!serialNumbers.find(
                (x) => (x.edit_value ?? x.SerialNumber) === (sn.edit_value ?? sn.SerialNumber) && x.id !== sn.id
            );

        const serial_numbers: ISerialSubmission[] = result[curr].serial_numbers.map((sn) => ({
            ...sn,
            isDuplicate: isDuplicate(sn),
        }));

        return {
            ...acc,
            [curr]: {
                ...result[curr],
                serial_numbers,
                action: serial_numbers.some((sn) => sn.isDuplicate) ? 'verify' : result[curr].action,
            },
        };
    }, {} as Record<string, InstallationData>);

    return processedResult;
};

const ExtendedWarrantyContextProvider = ({ children }: PropsWithChildren) => {
    const [lockNameField, setLockNameField] = useState(false);
    const [lockDateField, setLockDateField] = useState(false);
    const [isProcessingFiles, setIsProcessingFiles] = useState(false);
    const [viewIndex, setViewIndex] = useState<ViewIndex>(1);
    const [installations, setInstallations] = useState<Record<string, InstallationData>>({});

    const [validateSerialNumbers, { isLoading: isValidateSerialNumbersLoading }] = useValidateSerialNumbersMutation();
    const [saveSerialNumbers, { isLoading: isSaveSerialNumbersLoading }] = useSaveSerialNumbersMutation();

    const validateInstallations = async (installationsInput: InstallationDto) => {
        let result: Record<string, InstallationData> = {};
        const installationNames = Object.keys(installationsInput);
        if (installationNames && installationNames.length > 0 && !installationNames.find((v) => v === 'error')) {
            try {
                const areAllSave = Object.keys(installations).every(
                    (installation) => installations[installation].action !== 'verify'
                );

                for (const [insIndex, name] of installationNames.entries()) {
                    const serialNumbers = installationsInput[name].serial_numbers;
                    const installationAction = installations?.[name]?.action;
                    const date_installed = format(installationsInput[name].date, 'yyyy-MM-dd');

                    if (serialNumbers && serialNumbers.length <= 0) {
                        result[name] = {
                            id: installationsInput[name].id,
                            serial_numbers: [],
                            action: 'complete',
                            date_installed,
                            support: !!installationsInput[name].support,
                        };
                        continue;
                    }

                    try {
                        switch (installationAction) {
                            case 'save':
                                // We only want to execute the saving when all installations are validated/verified
                                if (areAllSave) {
                                    result[name] = {
                                        ...installations[name],
                                        action: 'complete',
                                        saved_data: await saveSerialNumbers({
                                            extended_warranty_request_id: installationsInput[name].id,
                                            installation_name: name,
                                            serial_numbers: installationsInput[name].serial_numbers,
                                            date_installed: installationsInput[name].date,
                                            support: installationsInput[name].support,
                                        }).unwrap(),
                                    };
                                } else {
                                    result[name] = installations[name];
                                }
                                break;
                            case 'complete':
                                result[name] = installations[name];
                                break;
                            case 'verify':
                            default:
                                result[name] = {
                                    id: installationsInput[name].id,
                                    serial_numbers: (
                                        await validateSerialNumbers({
                                            extended_warranty_request_id: installationsInput[name].id,
                                            installation_name: name,
                                            date_installed: installationsInput[name].date,
                                            support: installationsInput[name].support,
                                            serial_numbers: installationsInput[name].serial_numbers,
                                        }).unwrap()
                                    ).data.map((sn, i) => ({
                                        ...sn,
                                        id: `inst-${insIndex}-sn-${i}`,
                                    })),
                                    action: 'save',
                                    date_installed,
                                    support: !!installationsInput[name].support,
                                };
                        }
                    } catch (err) {
                        const error: EWValidationError = err as EWValidationError;

                        if (error.status == 400) {
                            result[name] = {
                                id: installationsInput[name].id,
                                date_installed,
                                support: installationsInput[name].support,
                                serial_numbers: error.data.data.map((sn, i) => ({
                                    ...sn,
                                    id: `inst-${insIndex}-sn-${i}`,
                                })),
                                action: 'verify',
                            };
                            result = processDuplicates(result);
                        } else {
                            throw err;
                        }
                    }
                }

                result = processDuplicates(result);

                return {
                    status:
                        installationNames
                            .map((name) =>
                                result[name].serial_numbers.some((sn) => !!sn.ValidationMessageError || sn.isDuplicate)
                            )
                            .filter((sn) => !!sn).length > 0
                            ? 'NOK'
                            : 'OK',
                    result,
                };
            } catch (err) {
                throw err;
            }
        }

        return { status: 'OK', result };
    };

    const formikInit = useFormik<ExtendedWarrantyFormData>({
        initialValues: {
            installation_name: '',
            date_installed: null,
            serial_numbers: '',
            extendedWarrantyFiles: [],
            uploadByFile: false,
            support: false,
        },
        validateOnMount: true,
        validationSchema: BulkSerialUploadSchema,
        onSubmit: async (values) => {
            setIsProcessingFiles(true);
            const { sheetIndex } = EXCEL_VALIDATION_CONFIG;
            let installationsDto: InstallationDtoByFile = {};

            try {
                if (formikInit.values.uploadByFile && values.extendedWarrantyFiles?.length) {
                    let failed = false;
                    await Promise.all(
                        values.extendedWarrantyFiles.map(async (fileRecord, i) => {
                            const currentFiles = [...(values.extendedWarrantyFiles ?? [])];
                            currentFiles[i].isLoading = true;
                            formikInit.setFieldValue('extendedWarrantyFiles', currentFiles);

                            const fileArrayBuffer = await fileRecord.file.arrayBuffer();
                            const workbook = XLSX.read(fileArrayBuffer, {
                                type: 'array',
                            });

                            const sheet = workbook.Sheets[workbook.SheetNames[sheetIndex]];
                            const jsonData = getJsonData(sheet);

                            let fileInstallations: InstallationDtoByFile = {};
                            try {
                                for (const [_rowIndex, row] of jsonData.entries()) {
                                    try {
                                        const cols = Object.keys(row);
                                        const baseError = 'Error in cell ';
                                        cols.forEach((cell, colIndex) => {
                                            const cellValue = row[cell];
                                            const colError = baseError + `${cell}: `;

                                            switch (colIndex) {
                                                case EXCEL_COL_ORDER.INSTALLATION_DATE:
                                                    const date = parse(cellValue, 'yyyy-MM-dd', new Date());
                                                    const isDateDifferentInFile =
                                                        fileInstallations[row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]]
                                                            ?.date &&
                                                        !isEqual(
                                                            parse(
                                                                fileInstallations[
                                                                    row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]
                                                                ].date,
                                                                'yyyy-MM-dd',
                                                                new Date()
                                                            ),
                                                            date
                                                        );
                                                    const isDateDifferent =
                                                        installationsDto[row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]]
                                                            ?.date &&
                                                        !isEqual(
                                                            parse(
                                                                installationsDto[
                                                                    row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]
                                                                ].date,
                                                                'yyyy-MM-dd',
                                                                new Date()
                                                            ),
                                                            date
                                                        );

                                                    if (isDateDifferentInFile || isDateDifferent) {
                                                        throw (
                                                            colError +
                                                            `Different dates detected for installation '${
                                                                row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]
                                                            }'${
                                                                isDateDifferent
                                                                    ? `, found in file '${
                                                                          installationsDto[
                                                                              row[
                                                                                  cols[
                                                                                      EXCEL_COL_ORDER.INSTALLATION_NAME
                                                                                  ]
                                                                              ]
                                                                          ].fileId
                                                                      }'`
                                                                    : ''
                                                            }`
                                                        );
                                                    }
                                                    break;
                                                case EXCEL_COL_ORDER.SERIAL_NUMBER:
                                                    const allFileInstallationsSNS = Object.keys(
                                                        fileInstallations
                                                    ).flatMap((name) =>
                                                        fileInstallations[name].serial_numbers.map((sn) => ({
                                                            serial_number: sn.serial_number,
                                                            installation: name,
                                                            file: fileInstallations[name].fileId,
                                                        }))
                                                    );
                                                    const allInstallationsSNS = Object.keys(installationsDto).flatMap(
                                                        (name) =>
                                                            installationsDto[name].serial_numbers.map((sn) => ({
                                                                serial_number: sn.serial_number,
                                                                installation: name,
                                                                file: installationsDto[name].fileId,
                                                            }))
                                                    );
                                                    const foundSNFile = allFileInstallationsSNS.find(
                                                        (sn) => sn.serial_number === cellValue
                                                    );
                                                    const foundSN = allInstallationsSNS.find(
                                                        (sn) => sn.serial_number === cellValue
                                                    );
                                                    if (foundSNFile || foundSN) {
                                                        throw (
                                                            colError +
                                                            `Serial number '${cellValue}' was already defined for installation '${
                                                                (foundSNFile || foundSN)?.installation
                                                            }'${foundSN ? `, found in file '${foundSN.file}'` : ''}`
                                                        );
                                                    }

                                                    break;
                                                case EXCEL_COL_ORDER.WILL_PROVIDE_SUPPORT:
                                                    const answer = cellValue.toLowerCase() === 'y';

                                                    const isAnswerDifferentInFile =
                                                        fileInstallations[
                                                            row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]
                                                        ] &&
                                                        fileInstallations[row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]]
                                                            .support !== answer;
                                                    const isAnswerDifferent =
                                                        installationsDto[
                                                            row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]
                                                        ] &&
                                                        installationsDto[row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]]
                                                            .support !== answer;
                                                    if (isAnswerDifferentInFile || isAnswerDifferent) {
                                                        throw (
                                                            colError +
                                                            `Different answers for support detected for installation '${
                                                                row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]
                                                            }'${
                                                                isAnswerDifferent
                                                                    ? `, found in file '${
                                                                          installationsDto[
                                                                              row[
                                                                                  cols[
                                                                                      EXCEL_COL_ORDER.INSTALLATION_NAME
                                                                                  ]
                                                                              ]
                                                                          ].fileId
                                                                      }'`
                                                                    : ''
                                                            }`
                                                        );
                                                    }
                                                    break;
                                                default:
                                                    break;
                                            }
                                        });
                                        if (fileInstallations[row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]]) {
                                            fileInstallations[
                                                row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]
                                            ].serial_numbers.push({
                                                serial_number: row[cols[EXCEL_COL_ORDER.SERIAL_NUMBER]],
                                                fileId: fileRecord.file.name,
                                            });
                                        } else {
                                            fileInstallations[row[cols[EXCEL_COL_ORDER.INSTALLATION_NAME]]] = {
                                                date: format(
                                                    parse(
                                                        row[cols[EXCEL_COL_ORDER.INSTALLATION_DATE]],
                                                        'yyyy-MM-dd',
                                                        new Date()
                                                    ),
                                                    'yyyy-MM-dd'
                                                ),
                                                support:
                                                    row[cols[EXCEL_COL_ORDER.WILL_PROVIDE_SUPPORT]].toLowerCase() ===
                                                    'y',
                                                serial_numbers: [
                                                    {
                                                        serial_number: row[cols[EXCEL_COL_ORDER.SERIAL_NUMBER]],
                                                        fileId: fileRecord.file.name,
                                                    },
                                                ],
                                                fileId: fileRecord.file.name,
                                            };
                                        }
                                    } catch (err) {
                                        fileInstallations = {};
                                        failed = true;
                                        currentFiles[i].isLoading = false;
                                        currentFiles[i].error = err as string;
                                        formikInit.setFieldValue('extendedWarrantyFiles', currentFiles);
                                        break;
                                    }
                                }
                                if (Object.keys(fileInstallations).length > 0) {
                                    installationsDto = {
                                        ...installationsDto,
                                        ...Object.keys(fileInstallations).reduce(
                                            (acc, newInstallation) => ({
                                                ...acc,
                                                [newInstallation]: {
                                                    ...(installationsDto[newInstallation] ??
                                                        fileInstallations[newInstallation]),
                                                    serial_numbers: [
                                                        ...(installationsDto[newInstallation]?.serial_numbers ?? []),
                                                        ...fileInstallations[newInstallation].serial_numbers,
                                                    ],
                                                },
                                            }),
                                            {} as InstallationDto
                                        ),
                                    };
                                    currentFiles[i].isLoading = false;
                                    currentFiles[i].error = '';
                                    formikInit.setFieldValue('extendedWarrantyFiles', currentFiles);
                                }
                            } catch (err) {
                                currentFiles[i].isLoading = false;
                                currentFiles[i].error = err as string;
                                formikInit.setFieldValue('extendedWarrantyFiles', currentFiles);
                            }
                        })
                    ).catch((err) => {
                        failed = true;
                        console.error(err);
                    });
                    if (failed) {
                        throw { msg: 'There were problems processing your files' };
                    }
                } else {
                    if (values.installation_name && values.date_installed) {
                        values.serial_numbers?.split(/[,\n\r]+/).forEach((serial_number) => {
                            installationsDto[values.installation_name as string] = {
                                id: values.id,
                                date: format(values.date_installed as Date, 'yyyy-MM-dd'),
                                support: !!values.support,
                                serial_numbers: [
                                    ...(installationsDto[values.installation_name as string]?.serial_numbers ?? []),
                                    {
                                        serial_number,
                                    },
                                ],
                            };
                        });
                    }
                }
                const { result, status } = await validateInstallations(installationsDto);

                setInstallations(result);
                setViewIndex(2);
                if (status === 'NOK') {
                    handleError('Some serial numbers are incorrect', { msg: '' }, { className: 'mb-20 md:mb-0' });
                } else {
                    toaster.success(
                        { msg: 'Serial numbers verified. Confirm to save.' },
                        { className: 'mb-20 md:mb-0' }
                    );
                }
            } catch (error) {
                handleError('Error processing serial numbers', error, { className: 'mb-20 md:mb-0' });
            } finally {
                setIsProcessingFiles(false);
            }
        },
    });

    const calculateFileName = useCallback(
        (file: CustomFile, iteration = 0) => {
            const fileName = file.customName ?? file.name;
            const newFileName = `${fileName.split('.')[0]}${iteration > 0 ? ` (${iteration})` : ''}.${
                fileName.split('.')[1]
            }`;
            if (!formikInit.values.extendedWarrantyFiles?.map((f) => f.name).includes(newFileName)) return newFileName;

            return calculateFileName(file, iteration + 1);
        },
        [formikInit.values.extendedWarrantyFiles]
    );

    const getJsonData = (sheet: XLSX.WorkSheet) => {
        const { startingCol, startingRow } = EXCEL_VALIDATION_CONFIG;
        const range = `${startingCol}${startingRow + 1}:${sheet['!ref']?.split(':')[1]}`;
        const decodedRange = XLSX.utils.decode_range(range);

        const jsonData = [];
        for (let rowNum = decodedRange.s.r; rowNum <= decodedRange.e.r; rowNum++) {
            const row: Record<string, string> = {};
            let hasNonNullValue = false;

            for (let colNum = decodedRange.s.c; colNum <= EXCEL_COL_ORDER.WILL_PROVIDE_SUPPORT; colNum++) {
                const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: colNum });
                const cell = sheet[cellAddress];

                if (cell && cell.t === 'n') {
                    row[cellAddress] = XLSX.SSF.format('yyyy-mm-dd', cell.v);
                } else {
                    row[cellAddress] = cell ? cell.w : ''; // Include the cell value or empty string for empty cells
                }

                if (cell && cell.w !== null && cell.w !== undefined) {
                    hasNonNullValue = true; // Mark that this row has at least one non-null value
                }
            }

            // Add the row to the result, even if it has empty cells, but ensure there's at least one non-null value
            if (hasNonNullValue) {
                jsonData.push(row);
            }
        }
        return jsonData;
    };

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            try {
                const { sheetIndex, startingRow, expectedHeaders } = EXCEL_VALIDATION_CONFIG;

                const mappedFiles = acceptedFiles.map((file, i) => ({ id: i, file }));

                const fileInfos = await Promise.all(
                    mappedFiles.map(async ({ file, id }): Promise<IExtendedWarrantyFile | string> => {
                        if (formikInit.values.extendedWarrantyFiles?.map((f) => f.name).includes(file.name)) {
                            return `${file.name}: A file with this name was already uploaded`;
                        } else if (mappedFiles.map((f) => (f.id === id ? null : f.file.name)).includes(file.name)) {
                            return `${file.name}: A fie with the same name was detected upon uploading`;
                        }
                        const fileArrayBuffer = await file.arrayBuffer();
                        const workbook = XLSX.read(fileArrayBuffer, {
                            type: 'array',
                        });
                        const sheet = workbook.Sheets[workbook.SheetNames[sheetIndex]];

                        // Check each column in the expectedHeaders config
                        for (const [col, requiredTerms] of Object.entries(expectedHeaders)) {
                            const cellRef = `${col}${startingRow}`;
                            const cellValue = sheet[cellRef]?.v?.toString().toLowerCase().trim() || '';

                            const allTermsFound = requiredTerms.every((term) => cellValue.includes(term));

                            if (!allTermsFound) {
                                return `${file.name}: Invalid excel structure format`;
                            }
                        }

                        const jsonData = getJsonData(sheet);

                        try {
                            if (!jsonData || jsonData.length === 0) throw 'The file has no installations';
                            jsonData.forEach((row) => {
                                try {
                                    const baseError = 'Error in cell ';
                                    Object.keys(row).forEach((cell, colIndex) => {
                                        const cellValue: string = row[cell];
                                        const colError = baseError + `${cell} - `;

                                        switch (colIndex) {
                                            case EXCEL_COL_ORDER.INSTALLATION_DATE:
                                                const date = parse(cellValue, 'yyyy-MM-dd', new Date());

                                                if (!isValid(date)) {
                                                    throw colError + 'Invalid Date, must be yyyy-mm-dd';
                                                } else if (isAfter(date, addDays(new Date(), 7))) {
                                                    throw colError + "Date can't be further than 7 days";
                                                }
                                                break;
                                            case EXCEL_COL_ORDER.SERIAL_NUMBER:
                                                if (!SERIAL_NUMBER_REGEX.test(cellValue)) {
                                                    throw colError + 'Wrong serial number format, must be HQXXXXXXXXX';
                                                }
                                                break;
                                            case EXCEL_COL_ORDER.WILL_PROVIDE_SUPPORT:
                                                if (!['y', 'n'].includes(cellValue.toLowerCase())) {
                                                    throw colError + 'Wrong input for provide support, must be Y or N';
                                                }
                                                break;
                                            case EXCEL_COL_ORDER.INSTALLATION_NAME:
                                                if (!cellValue || cellValue === '')
                                                    throw colError + 'Installation name must not be empty';
                                                break;
                                            default:
                                                break;
                                        }
                                    });
                                } catch (err) {
                                    throw err;
                                }
                            });
                        } catch (err) {
                            return `${file.name}: ${err}`;
                        }

                        return {
                            file,
                            name: file.name,
                        };
                    })
                );

                const errors = fileInfos.filter((fileInfo) => typeof fileInfo === 'string');
                const newFileInfos = formikInit.values.extendedWarrantyFiles?.concat(
                    fileInfos.filter((fileInfo) => typeof fileInfo !== 'string')
                );
                formikInit.setFieldValue('extendedWarrantyFiles', newFileInfos);
                if (errors.length > 0) {
                    throw errors;
                }
            } catch (err) {
                if (Array.isArray(err)) {
                    const error = err as string[];
                    handleError('The following files threw errors:', { msg: error.map((x) => <p>{x}</p>) });
                } else {
                    handleError('Unexpected error', (err as string).toString());
                }
            }
        },
        [formikInit]
    );

    const onDropRejected = () => toaster.failure({ msg: 'Only .xls and .xlsx file extensions are allowed' });

    const dropzoneState = useDropzone({
        onDrop,
        onDropRejected,
        accept: {
            'application/vnd.ms-excel': [],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
        },
        multiple: true,
    });

    return (
        <ExtendedWarrantyContext.Provider
            value={{
                formik: formikInit,
                dropzoneState,
                lockNameField,
                setLockNameField,
                lockDateField,
                setLockDateField,
                isProcessingFiles,
                setIsProcessingFiles,
                isValidateSerialNumbersLoading,
                isSaveSerialNumbersLoading,
                viewIndex,
                setViewIndex,
                installations,
                setInstallations,
                validateInstallations,
                processDuplicates,
            }}
        >
            {children}
        </ExtendedWarrantyContext.Provider>
    );
};

export { ExtendedWarrantyContext, ExtendedWarrantyContextProvider, useExtendedWarrantyContext };
